import { Materialized } from "@silversea-ssc/functional-support"
import { Schema } from "effect/Schema"
import { ActionTypes, ActorRefFrom, DoneInvokeEvent, InterpreterFrom, StateMachine } from "xstate"
import { BadRequestCode } from "@ssc-booking/api-contracts/src/bad-request-response"

export type InitialDataLoadContext = {
    id: string
}

type LoadedResponse<ASuccess> = {
    response: ASuccess
}

export type LoadedContext<ASuccess> = InitialDataLoadContext & LoadedResponse<ASuccess>

type Error<T> = {
    type: "ERROR"
    error: T
}
type FatalError = {
    type: "FATAL"
    errorCode: FatalErrorCode
    message: string
    info: unknown
}

type ErrorResponse<AError> = {
    error: Error<AError> | FatalError
}

export type ErrorContext<AError> = InitialDataLoadContext & ErrorResponse<AError>

export type DataLoadTypeState<ASuccess, AError> =
    | { value: "idle"; context: InitialDataLoadContext }
    | { value: "loading"; context: InitialDataLoadContext }
    | { value: "loading.firstLoad"; context: InitialDataLoadContext }
    | { value: "loading.refresh"; context: LoadedContext<ASuccess> }
    | { value: "loaded"; context: LoadedContext<ASuccess> }
    | { value: "error"; context: ErrorContext<AError> }

export type DataLoadContext<ASuccess, AError> = InitialDataLoadContext &
    Partial<LoadedResponse<ASuccess>> &
    Partial<ErrorResponse<AError>>

export type LoadSuccess<ASuccess> = { type: "LOAD_SUCCESS"; response: ASuccess }
export type LoadError<AError> = { type: "LOAD_ERROR"; response: AError }
export type FatalErrorCode = "connectionClosed" | "parsing" | "unknownResponse" | BadRequestCode
export type LoadFatalError = { type: "LOAD_FATAL_ERROR"; errorCode: FatalErrorCode; message: string; info: unknown }
export type Load<AReq> = { type: "LOAD"; data: AReq; country: string }
export type DataLoadEvents<AReq, ASuccess, AError> =
    | Load<AReq>
    | LoadSuccess<ASuccess>
    | LoadError<AError>
    | LoadFatalError
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    | (DoneInvokeEvent<any> & { type: ActionTypes.DoneInvoke })

export type DataLoadStateMachine<AReq, ASuccess, AError> = StateMachine<
    DataLoadContext<ASuccess, AError>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    any,
    DataLoadEvents<AReq, ASuccess, AError>,
    DataLoadTypeState<ASuccess, AError>
>

type DataLoadInterpreter<AReq, ASuccess, AError> = InterpreterFrom<DataLoadStateMachine<AReq, ASuccess, AError>>
type DataLoadState<AReq, ASuccess, AError> = DataLoadInterpreter<AReq, ASuccess, AError>["state"]
type DataLoadSend<AReq, ASuccess, AError> = DataLoadInterpreter<AReq, ASuccess, AError>["send"]
type ActorRef<AReq, ASuccess, AError> = ActorRefFrom<DataLoadStateMachine<AReq, ASuccess, AError>>
type DataLoadTypes<AReq, ASuccess, AError> = {
    Context: DataLoadContext<ASuccess, AError>
    LoadedContext: LoadedContext<ASuccess>
    ErrorContext: ErrorContext<AError>
    Events: DataLoadEvents<AReq, ASuccess, AError>
    LoadSuccess: LoadSuccess<ASuccess>
    LoadError: LoadError<AError>
    LoadFatalError: LoadFatalError
    Load: Load<AReq>
    TypeState: DataLoadTypeState<ASuccess, AError>
    StateMachine: DataLoadStateMachine<AReq, ASuccess, AError>
    Interpreter: DataLoadInterpreter<AReq, ASuccess, AError>
    State: DataLoadState<AReq, ASuccess, AError>
    Send: DataLoadSend<AReq, ASuccess, AError>
    ActorRef: ActorRef<AReq, ASuccess, AError>
}
export const dataLoadTypes = <EReq, AReq, ESuccess, ASuccess, EError, AError>(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    Request: Materialized<EReq, AReq>,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    Success: Materialized<ESuccess, ASuccess>,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    Error: Materialized<EError, AError>,
): DataLoadTypes<AReq, ASuccess, AError> => ({}) as DataLoadTypes<AReq, ASuccess, AError>

export const dataLoadTypesEffect = <EReq, AReq, ESuccess, ASuccess, EError, AError>(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    Request: Schema<EReq, AReq>,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    Success: Schema<ESuccess, ASuccess>,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    Error: Schema<EError, AError>,
): DataLoadTypes<AReq, ASuccess, AError> => ({}) as DataLoadTypes<AReq, ASuccess, AError>
